import { useCallback, useEffect, useRef, useState } from 'react';

import { timeout } from '@willow/shared-iso';
import { PortalRouterMatch } from '@willow/types-iso';

import { CheckingScreen } from './CheckingScreen';
import { EmailScreen } from './EmailScreen';
import { FIND_PORTAL_HANDOFF_MS, FIND_PORTAL_PACING_MS } from './findPortalConfig';
import { LimitedScreen } from './LimitedScreen';
import { LookupOutcome, lookupPortals } from './lookupPortals';
import { MultiMatchScreen } from './MultiMatchScreen';
import { NoMatchScreen } from './NoMatchScreen';
import { SingleMatchScreen } from './SingleMatchScreen';

type Screen = 'email' | 'checking' | 'single' | 'multi' | 'none' | 'limited';

interface Props {
  pacingMs?: number;
  handoffMs?: number;
  lookup?: (email: string) => Promise<LookupOutcome>;
  navigate?: (url: string) => void;
}

const defaultNavigate = (url: string) => window.location.assign(url);

export const FindPortalPage = ({
  pacingMs = FIND_PORTAL_PACING_MS,
  handoffMs = FIND_PORTAL_HANDOFF_MS,
  lookup = (email: string) => lookupPortals(email),
  navigate = defaultNavigate,
}: Props) => {
  const [screen, setScreen] = useState<Screen>('email');
  const [email, setEmail] = useState('');
  const [matches, setMatches] = useState<PortalRouterMatch[]>([]);
  const [retryAfterSeconds, setRetryAfterSeconds] = useState(0);
  const [lookupFailed, setLookupFailed] = useState(false);
  const handoffTimer = useRef<ReturnType<typeof setTimeout>>();

  const clearHandoffTimer = useCallback(() => {
    if (handoffTimer.current !== undefined) {
      clearTimeout(handoffTimer.current);
      handoffTimer.current = undefined;
    }
  }, []);

  useEffect(() => clearHandoffTimer, [clearHandoffTimer]);

  const runLookup = useCallback(
    async (candidate: string) => {
      setScreen('checking');
      try {
        // The checking screen is held for pacingMs whatever the outcome, so a no-match takes
        // exactly as long as a match does.
        const [outcome] = await Promise.all([lookup(candidate), timeout(pacingMs)]);
        if (outcome.kind === 'RATE_LIMITED') {
          setRetryAfterSeconds(outcome.retryAfterSeconds);
          setScreen('limited');
          return;
        }
        setMatches(outcome.portals);
        if (outcome.portals.length === 0) {
          setScreen('none');
        } else if (outcome.portals.length === 1) {
          setScreen('single');
        } else {
          setScreen('multi');
        }
      } catch {
        setLookupFailed(true);
        setScreen('email');
      }
    },
    [lookup, pacingMs],
  );

  const onSubmitEmail = useCallback(
    (candidate: string) => {
      setEmail(candidate);
      setLookupFailed(false);
      void runLookup(candidate);
    },
    [runLookup],
  );

  const backToEmail = useCallback(() => {
    clearHandoffTimer();
    setScreen('email');
  }, [clearHandoffTimer]);

  useEffect(() => {
    if (screen !== 'single' || matches.length === 0) {
      return undefined;
    }
    const target = matches[0].loginUrl;
    handoffTimer.current = setTimeout(() => navigate(target), handoffMs);
    return clearHandoffTimer;
  }, [clearHandoffTimer, handoffMs, matches, navigate, screen]);

  useEffect(() => {
    if (screen !== 'limited') {
      return undefined;
    }
    if (retryAfterSeconds <= 0) {
      setScreen('email');
      return undefined;
    }
    const tick = setTimeout(() => setRetryAfterSeconds((current) => current - 1), 1000);
    return () => clearTimeout(tick);
  }, [retryAfterSeconds, screen]);

  const content = () => {
    switch (screen) {
      case 'checking':
        return <CheckingScreen email={email} />;
      case 'single':
        return (
          <SingleMatchScreen
            match={matches[0]}
            handoffMs={handoffMs}
            onGoNow={() => {
              clearHandoffTimer();
              navigate(matches[0].loginUrl);
            }}
            onUseDifferentEmail={backToEmail}
          />
        );
      case 'multi':
        return (
          <MultiMatchScreen
            matches={matches}
            onSelect={(match) => navigate(match.loginUrl)}
            onUseDifferentEmail={backToEmail}
          />
        );
      case 'none':
        return <NoMatchScreen email={email} onTryAnotherEmail={backToEmail} />;
      case 'limited':
        return <LimitedScreen remainingSeconds={retryAfterSeconds} />;
      case 'email':
      default:
        return <EmailScreen email={email} lookupFailed={lookupFailed} onSubmit={onSubmitEmail} />;
    }
  };

  return content();
};
